import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Inbox, 
  Send, 
  ArrowLeftRight, 
  Package, 
  PackageCheck,
  FileText, 
  BarChart3,
  ExternalLink,
  FileSearch,
  ClipboardCheck
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { filterOperations } from '@/utils/operationAccess';
import apiClient from '@/services/apiClient';

/**
 * GenericSectionDashboard - Reusable dashboard component for all warehouse sections
 * 
 * Features 6 standard tiles:
 * 1. Inward Consignment - Receive incoming consignments (external + internal transfers)
 * 2. Shipment Request - View and process outgoing shipment requests
 * 3. Internal Transfers - Handle transfers within/between sections
 * 4. Bundle Receipt Entry - Process received bundles
 * 5. Day Wise Transaction Report - Daily summary report
 * 6. Closing Stock Report - Live closing stock
 * 
 * Props:
 * - sectionName: Display name (e.g., "Inhouse Section")
 * - sectionId: URL-safe ID (e.g., "inhouse")
 * - sectionKey: Database key (e.g., "Inhouse")
 * - description: Section description
 * - backRoute: Route to go back to (default: "/")
 * - customTiles: Optional array to override/add custom tiles
 * - hideTiles: Optional array of tile IDs to hide
 * - moduleId: Optional backend module_id used by User Management's
 *   allowed_operations gate. Defaults to sectionId (with dashes converted to
 *   underscores) so `shirt-manufacturing` → `shirt_manufacturing`,
 *   `sales-return` → `sales_return`, etc.
 */

const GenericSectionDashboard = ({ 
  sectionName, 
  sectionId, 
  sectionKey,
  description = "Consignments, Transfers & Reports",
  backRoute = "/",
  customTiles = [],
  hideTiles = [],
  moduleId,
  headerExtras = null
}) => {
  const navigate = useNavigate();
  // Sections that raise Purchase Orders get the 'Purchase Receipt' screen.
  const [raisesPos, setRaisesPos] = useState(false);
  const poSection = sectionKey || sectionName;
  useEffect(() => {
    apiClient.get('/po-receipts/raises-pos', { params: { section: poSection } })
      .then((r) => setRaisesPos(!!r.data.raises)).catch(() => setRaisesPos(false));
  }, [poSection]);
  
  const basePath = `/sections/${sectionId}`;
  
  // Standard 6 tiles for all sections
  const defaultTiles = [
    {
      id: 'inward-consignment',
      title: 'Inward Consignment',
      description: 'Receive and process incoming consignments',
      icon: Inbox,
      iconBg: 'bg-teal-500',
      route: `${basePath}/inward-consignment`,
      testId: 'inward-consignment-tile'
    },
    {
      id: 'shipment-request',
      title: 'Shipment Request',
      description: 'View and process incoming shipment requests',
      icon: Send,
      iconBg: 'bg-orange-500',
      route: `${basePath}/shipment-request`,
      testId: 'shipment-request-tile'
    },
    {
      id: 'internal-transfer',
      title: 'Internal Transfers',
      description: 'Handle transfers within/between sections',
      icon: ArrowLeftRight,
      iconBg: 'bg-indigo-500',
      route: `${basePath}/internal-transfer`,
      testId: 'internal-transfer-tile'
    },
    {
      id: 'bundle-receipt',
      title: 'Bundle Receipt Entry',
      description: 'Process received bundles',
      icon: Package,
      iconBg: 'bg-emerald-500',
      route: `${basePath}/bundle-receipt`,
      testId: 'bundle-receipt-tile'
    },
    ...(raisesPos ? [{
      id: 'purchase-receipt',
      title: 'Purchase Receipt',
      description: 'Record goods received against a Purchase Order',
      icon: PackageCheck,
      iconBg: 'bg-amber-600',
      route: `${basePath}/purchase-receipt?section=${encodeURIComponent(poSection)}`,
      testId: 'purchase-receipt-tile'
    }] : []),
    {
      id: 'day-wise-report',
      title: 'Day Wise Transaction Report',
      description: 'Daily summary report',
      icon: FileText,
      iconBg: 'bg-slate-600',
      route: `${basePath}/day-wise-report`,
      testId: 'day-wise-report-tile'
    },
    {
      id: 'closing-stock',
      title: 'Closing Stock Report',
      description: 'Live closing stock by operation',
      icon: BarChart3,
      iconBg: 'bg-teal-600',
      route: `${basePath}/closing-stock`,
      testId: 'closing-stock-tile'
    },
    {
      id: 'misc-outward',
      title: 'Misc Outward',
      description: 'Send goods outward for sample/misc purposes',
      icon: ExternalLink,
      iconBg: 'bg-rose-500',
      route: `${basePath}/misc-outward`,
      testId: 'misc-outward-tile'
    },
    {
      id: 'inventory-report',
      title: 'Inventory Report',
      description: 'View current section inventory with filters & export',
      icon: BarChart3,
      iconBg: 'bg-indigo-600',
      route: `${basePath}/inventory-report`,
      testId: 'inventory-report-tile'
    },
    {
      id: 'internal-transfer-report',
      title: 'Internal Transfer Report',
      description: 'Transfers out of this section — qty, ERP entry no & print sheets',
      icon: ArrowLeftRight,
      iconBg: 'bg-purple-600',
      route: `${basePath}/it-report`,
      testId: 'internal-transfer-report-tile'
    },
    {
      id: 'documents-report',
      title: 'Documents Report',
      description: 'Documents received — completed once every bundle has left the section',
      icon: FileText,
      iconBg: 'bg-emerald-600',
      route: `${basePath}/documents-report`,
      testId: 'documents-report-tile'
    }
  ];
  
  // Filter out hidden tiles and merge with custom tiles
  // Priority tiles go first
  const priorityTiles = customTiles.filter(tile => tile.priority);
  const normalCustomTiles = customTiles.filter(tile => !tile.priority);
  const mergedTiles = [
    ...priorityTiles,
    ...defaultTiles.filter(tile => !hideTiles.includes(tile.id)),
    ...normalCustomTiles
  ];

  // Gate tiles by the logged-in user's allowed_operations (set in User
  // Management → Modules & Operations). The backend module_id uses
  // underscores (e.g. `shirt_manufacturing`) while URL-safe sectionIds use
  // dashes. Fall back to an auto-derived module_id so existing dashboards
  // that don't pass the prop still benefit from gating.
  const effectiveModuleId = moduleId || (sectionId || '').replace(/-/g, '_');
  const tiles = filterOperations(effectiveModuleId, mergedTiles);

  // Global read-only lookup — always available on every module dashboard.
  const globalTiles = [
    {
      id: 'fetch-document-number',
      title: 'Fetch Document Number',
      description: 'Scan a bundle to trace its inward document, gate-in, section receipt & accounts handover',
      icon: FileSearch,
      iconBg: 'bg-sky-600',
      route: '/reports/fetch-document-number',
      testId: 'fetch-document-number-tile',
    },
    ...((sectionId === 'accounts' || raisesPos) ? [{
      id: 'grn',
      title: 'Goods Receipt Notes',
      description: sectionId === 'accounts' ? 'GRN of every supplier delivery at PO rates + GST — download and tick booked' : 'Finalise the GRN of a delivery once its goods are counted on the PO',
      icon: ClipboardCheck,
      iconBg: 'bg-emerald-700',
      route: sectionId === 'accounts' ? '/po/grn?tab=list' : '/po/grn',
      testId: 'grn-tile',
    }] : []),
  ];
  const allTiles = [...tiles, ...globalTiles];

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      {/* Header */}
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              onClick={() => navigate(backRoute)} 
              className="text-[#F9FAFB] hover:bg-white/10"
              data-testid="back-button"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">{sectionName}</h1>
              <p className="text-sm text-[#E8DCC8]">{description}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Section-specific extras (e.g. flow-summary tiles) above the
            tile grid. Optional — sections can omit. */}
        {headerExtras && (
          <div className="mb-6" data-testid="section-header-extras">{headerExtras}</div>
        )}
        {/* Section Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1A1F2E] font-display">Section Management</h2>
          <p className="text-gray-600 mt-1">Consignments, Transfers & Reports</p>
        </div>

        {/* Tiles Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
          {allTiles.map((tile) => {
            const IconComponent = tile.icon;
            return (
              <Card 
                key={tile.id}
                className="bg-white hover:shadow-lg transition-all duration-200 cursor-pointer border-0 shadow-md"
                onClick={() => navigate(tile.route)}
                data-testid={tile.testId}
              >
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <div className={`w-16 h-16 ${tile.iconBg} rounded-2xl flex items-center justify-center mb-4 shadow-md`}>
                    <IconComponent className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm leading-tight mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed">
                    {tile.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </main>
    </div>
  );
};

export default GenericSectionDashboard;
